import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import dayjs from 'dayjs';
import { Button, DateRangeFilter, showErrorToast } from '@/components';
import {
  exportInvoices,
  invoiceStatusGroupParam,
  InvoiceExportFormat,
} from '@/services/api/invoices';

interface InvoiceExportModalProps {
  open: boolean;
  onClose: () => void;
  clientId?: string | number;
}

// Date presets — each resolves to a concrete issueDateFrom/issueDateTo range
// (the backend no longer accepts a preset keyword).
const presetRange = (fromDaysAgo: number, toDaysAgo = 0) => ({
  start: dayjs().subtract(fromDaysAgo, 'day').format('YYYY-MM-DD'),
  end: dayjs().subtract(toDaysAgo, 'day').format('YYYY-MM-DD'),
});

const DATE_PRESETS: {
  label: string;
  range: () => { start: string; end: string };
}[] = [
  { label: 'Today', range: () => presetRange(0, 0) },
  { label: 'Last 7 days', range: () => presetRange(7, 0) },
  { label: 'Last 30 days', range: () => presetRange(30, 0) },
];

const FORMATS: { value: InvoiceExportFormat; label: string; icon: string }[] = [
  { value: 'csv', label: 'CSV', icon: 'ri-file-text-line' },
  { value: 'xlsx', label: 'Excel (XLSX)', icon: 'ri-file-excel-2-line' },
];

// Status groups — values come from the shared source of truth so the export's
// `status` param always matches the list tabs; only the labels are local.
const STATUS_OPTIONS: { key: string; label: string; statuses: string }[] = [
  {
    key: 'IN_PIPELINE',
    label: 'In Pipeline',
    statuses: invoiceStatusGroupParam('IN_PIPELINE'),
  },
  {
    key: 'WAITING_CONFIRMATION',
    label: 'Awaiting Confirm',
    statuses: invoiceStatusGroupParam('WAITING_CONFIRMATION'),
  },
  {
    key: 'DISPUTED',
    label: 'Disputed',
    statuses: invoiceStatusGroupParam('DISPUTED'),
  },
  {
    key: 'RESOLVED',
    label: 'Resolved',
    statuses: invoiceStatusGroupParam('RESOLVED'),
  },
];

const toISODate = (d: Date | null) => (d ? dayjs(d).format('YYYY-MM-DD') : '');
const fromISODate = (s: string) => (s ? new Date(s) : null);

// Lightweight hover/focus tooltip (no extra deps — same group-hover pattern used
// elsewhere in the app). Reveals on pointer hover and keyboard focus.
const InfoTooltip: React.FC<{ label: string; children: React.ReactNode }> = ({
  label,
  children,
}) => (
  <span className="group relative inline-flex">
    <button
      type="button"
      aria-label={label}
      className="inline-flex text-text-soft-400 transition-colors hover:text-text-sub-800 focus:outline-none focus-visible:text-text-sub-600"
    >
      <i className="ri-information-line text-sm" />
    </button>
    <span
      role="tooltip"
      className="pointer-events-none absolute left-0 top-full z-10 mt-1.5 hidden w-72 rounded-lg bg-bg-strong-950 px-3 py-2 text-[13px] font-normal normal-case leading-relaxed tracking-normal text-static-white shadow-card-md group-hover:block group-focus-within:block"
    >
      {children}
    </span>
  </span>
);

const InvoiceExportModal: React.FC<InvoiceExportModalProps> = ({
  open,
  onClose,
  clientId,
}) => {
  const [format, setFormat] = useState<InvoiceExportFormat>('csv');
  const [statusKeys, setStatusKeys] = useState<string[]>([]);
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [exporting, setExporting] = useState(false);

  // Reset to defaults each time the modal opens. Status starts empty — the
  // backend then applies its own default (Waiting Confirmation + Resolved).
  useEffect(() => {
    if (open) {
      setFormat('csv');
      setStatusKeys([]);
      setFrom('');
      setTo('');
      setExporting(false);
    }
  }, [open]);

  if (!open) return null;

  const toggleStatus = (key: string) =>
    setStatusKeys((keys) =>
      keys.includes(key) ? keys.filter((k) => k !== key) : [...keys, key]
    );

  // Presets just fill in the date range; the picker can still fine-tune it.
  const applyPreset = (r: { start: string; end: string }, active: boolean) => {
    if (active) {
      setFrom('');
      setTo('');
    } else {
      setFrom(r.start);
      setTo(r.end);
    }
  };

  const handleExport = async () => {
    // Join every selected group's statuses into the comma-separated param.
    const statusParam = STATUS_OPTIONS.filter((o) => statusKeys.includes(o.key))
      .map((o) => o.statuses)
      .join(',');

    setExporting(true);
    try {
      await exportInvoices({
        clientId,
        status: statusParam || undefined,
        format,
        issueDateFrom: from || undefined,
        issueDateTo: to || undefined,
      });
      onClose();
    } catch (err) {
      showErrorToast(
        'Export gagal',
        err instanceof Error ? err.message : 'Terjadi kesalahan.'
      );
    } finally {
      setExporting(false);
    }
  };

  return createPortal(
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 z-40 bg-bg-strong-950/40"
        aria-hidden="true"
        onClick={exporting ? undefined : onClose}
      />

      {/* Centered modal */}
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <div className="w-full max-w-md rounded-xl bg-bg-white-0 shadow-card-lg">
          {/* Header */}
          <div className="flex items-start justify-between border-b border-stroke-soft-200 px-6 py-4">
            <div>
              <h2 className="text-lg font-semibold text-text-strong-950">
                Export invoices
              </h2>
              <p className="mt-0.5 text-xs text-text-sub-600">
                Pilih rentang tanggal terbit dan format file.
              </p>
            </div>
            <button
              type="button"
              onClick={onClose}
              disabled={exporting}
              className="text-text-soft-400 hover:text-text-sub-600 disabled:opacity-50"
              aria-label="Close"
            >
              <i className="ri-close-line text-xl" />
            </button>
          </div>

          {/* Body */}
          <div className="px-6 py-5">
            {/* Issue date */}
            <div>
              <div className="mb-2 flex items-center gap-1.5">
                <h4 className="text-xs font-semibold uppercase tracking-wide text-text-soft-400">
                  Issue date
                </h4>
                <InfoTooltip label="Penjelasan filter tanggal terbit">
                  📅 Rentang ini memfilter berdasarkan{' '}
                  <strong className="font-semibold">tanggal terbit</strong>{' '}
                  invoice — sama seperti yang dipakai di daftar.
                  <br />
                  <br />
                  Secara default hanya invoice{' '}
                  <strong className="font-semibold">
                    Waiting Confirmation
                  </strong>{' '}
                  dan <strong className="font-semibold">Resolved</strong> yang
                  diekspor — pilih status di bawah untuk menyertakan yang lain.
                  <br />
                  <br />
                  Tanggal dihitung dalam waktu Asia/Jakarta (WIB).
                </InfoTooltip>
              </div>
              <div className="mb-3 flex flex-wrap gap-2">
                {DATE_PRESETS.map((p) => {
                  const r = p.range();
                  const active = from === r.start && to === r.end;
                  return (
                    <button
                      key={p.label}
                      type="button"
                      onClick={() => applyPreset(r, active)}
                      className={`rounded-lg border px-3.5 py-[7px] text-[12.5px] font-semibold transition-colors ${
                        active
                          ? 'border-primary bg-primary text-static-white'
                          : 'border-stroke-sub-300 text-text-sub-600 hover:border-primary'
                      }`}
                    >
                      {p.label}
                    </button>
                  );
                })}
              </div>
              <DateRangeFilter
                key={`${from}-${to}`}
                onDateRangeChange={(range) => {
                  setFrom(toISODate(range.from));
                  setTo(toISODate(range.to));
                }}
                initialRange={{
                  from: fromISODate(from),
                  to: fromISODate(to),
                }}
              />
            </div>

            {/* Status */}
            <div className="mt-5">
              <h4 className="mb-2 text-xs font-semibold uppercase tracking-wide text-text-soft-400">
                Status
              </h4>
              <div className="space-y-1 rounded-lg border border-stroke-soft-200 p-3">
                {STATUS_OPTIONS.map((opt) => {
                  const checked = statusKeys.includes(opt.key);
                  return (
                    <label
                      key={opt.key}
                      className="flex cursor-pointer items-center gap-2.5 py-1"
                    >
                      <span
                        className={`flex h-4 w-4 shrink-0 items-center justify-center rounded border transition-colors ${
                          checked
                            ? 'border-primary bg-primary'
                            : 'border-stroke-sub-300 bg-bg-white-0'
                        }`}
                      >
                        <input
                          type="checkbox"
                          className="sr-only"
                          checked={checked}
                          onChange={() => toggleStatus(opt.key)}
                        />
                        {checked && (
                          <i className="ri-check-line text-xs text-static-white" />
                        )}
                      </span>
                      <span className="text-sm text-text-strong-950">
                        {opt.label}
                      </span>
                    </label>
                  );
                })}
              </div>
            </div>

            {/* Format */}
            <div className="mt-5">
              <h4 className="mb-2 text-xs font-semibold uppercase tracking-wide text-text-soft-400">
                Format
              </h4>
              <div className="grid grid-cols-2 gap-2">
                {FORMATS.map((f) => {
                  const active = format === f.value;
                  return (
                    <button
                      key={f.value}
                      type="button"
                      onClick={() => setFormat(f.value)}
                      className={`flex items-center gap-2 rounded-lg border px-4 py-3 text-sm font-medium transition-colors ${
                        active
                          ? 'border-primary bg-primary-lighter text-primary'
                          : 'border-stroke-sub-300 text-text-sub-600 hover:border-primary'
                      }`}
                    >
                      <i className={`${f.icon} text-lg`} />
                      {f.label}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="flex items-center justify-end gap-3 border-t border-stroke-soft-200 px-6 py-4">
            <Button variant="white" onClick={onClose} disabled={exporting}>
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={handleExport}
              disabled={exporting}
            >
              {exporting ? (
                <>
                  <i className="ri-loader-4-line mr-1.5 animate-spin" />
                  Exporting…
                </>
              ) : (
                <>
                  <i className="ri-download-2-line mr-1.5" />
                  Export
                </>
              )}
            </Button>
          </div>
        </div>
      </div>
    </>,
    document.body
  );
};

export default InvoiceExportModal;
